package com.fantasytenis.web.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.fantasytenis.shared.core.utils.SharedData
import com.fantasytenis.shared.domain.contests.entity.ContestStatus
import com.fantasytenis.shared.domain.contests.entity.ContestType
import com.fantasytenis.web.theme.Colors
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text

/**
 * Badge component for displaying contest types.
 * Provides consistent styling across the application.
 *
 * When showPaidGames is false, the FREE badge is not shown since all visible
 * contests are free (making the badge redundant).
 */
@Composable
fun ContestTypeBadge(type: ContestType) {
    val showPaidGames by SharedData.showPaidGames.collectAsState()

    // Don't show FREE badge when paid games are hidden (all contests are free anyway)
    if (type == ContestType.FREE && !showPaidGames) {
        return
    }

    val (label, bgColor) = when (type) {
        ContestType.GPP -> "GPP" to Colors.ContestType.GPP
        ContestType.HEAD_TO_HEAD -> "H2H" to Colors.ContestType.HeadToHead
        ContestType.FIFTY_FIFTY -> "50/50" to Colors.ContestType.FiftyFifty
        ContestType.MULTIPLIER -> "Multi" to Colors.ContestType.Multiplier
        ContestType.FREE -> "FREE" to Colors.ContestType.Free
    }

    Span({
        style {
            backgroundColor(bgColor)
            color(Colors.White)
            fontSize(11.px)
            fontWeight(600)
            padding(4.px, 8.px)
            borderRadius(4.px)
            display(DisplayStyle.InlineBlock)
        }
    }) {
        Text(label)
    }
}

/**
 * Badge component for displaying contest status.
 * Provides consistent styling across the application.
 */
@Composable
fun ContestStatusBadge(status: ContestStatus) {
    val (bgColor, textColor, label) = when (status) {
        ContestStatus.UPCOMING -> Triple(
            Colors.ContestStatus.UpcomingBackground,
            Colors.ContestStatus.UpcomingText,
            "Upcoming"
        )
        ContestStatus.LIVE -> Triple(
            Colors.ContestStatus.LiveBackground,
            Colors.ContestStatus.LiveText,
            "Live"
        )
        ContestStatus.COMPLETED -> Triple(
            Colors.ContestStatus.CompletedBackground,
            Colors.ContestStatus.CompletedText,
            "Completed"
        )
        ContestStatus.CANCELLED -> Triple(
            Colors.ContestStatus.CancelledBackground,
            Colors.ContestStatus.CancelledText,
            "Cancelled"
        )
    }

    Span({
        style {
            backgroundColor(bgColor)
            color(textColor)
            fontSize(12.px)
            fontWeight(600)
            padding(6.px, 12.px)
            borderRadius(4.px)
            display(DisplayStyle.InlineBlock)
        }
    }) {
        Text(label)
    }
}

/**
 * Generic badge component for custom labels.
 */
@Composable
fun Badge(
    text: String,
    backgroundColor: CSSColorValue = Colors.Gray200,
    textColor: CSSColorValue = Colors.TextPrimary,
    size: BadgeSize = BadgeSize.Medium
) {
    val (fontSize, paddingV, paddingH) = when (size) {
        BadgeSize.Small -> Triple(10.px, 2.px, 6.px)
        BadgeSize.Medium -> Triple(12.px, 4.px, 8.px)
        BadgeSize.Large -> Triple(14.px, 6.px, 12.px)
    }

    Span({
        style {
            backgroundColor(backgroundColor)
            color(textColor)
            fontSize(fontSize)
            fontWeight(600)
            padding(paddingV, paddingH)
            borderRadius(4.px)
            display(DisplayStyle.InlineBlock)
        }
    }) {
        Text(text)
    }
}

enum class BadgeSize {
    Small,
    Medium,
    Large
}

/**
 * Featured badge for highlighted contests.
 */
@Composable
fun FeaturedBadge() {
    Badge(
        text = "Featured",
        backgroundColor = Colors.Secondary,
        textColor = Colors.TextPrimary,
        size = BadgeSize.Small
    )
}

/**
 * Guaranteed badge for GPP contests.
 */
@Composable
fun GuaranteedBadge() {
    Badge(
        text = "Guaranteed",
        backgroundColor = Colors.SuccessLight,
        textColor = Colors.SuccessDark,
        size = BadgeSize.Small
    )
}
